// /partners/share/:shareId — when a recipient taps "Log it →" on an inbox
// item, they land here. We re-use the existing MealReview component to let
// them adjust portions before saving, then save the meal in THEIR account
// and mark the share accepted.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../components/BackButton";
import MealReview from "../components/MealReview";
import { acceptShare, loadInbox, type SharedMealInboxItem } from "../lib/sharing";
import { friendlyError } from "../lib/errors";
import { loadGoalsRemote, type Goals } from "../lib/goals";
import { useIsPro } from "../lib/subscription";
import { loadMealsRemote, todayISO, localDateOf } from "../lib/storage";
import { sumTotals, type MealEstimate, type MealType } from "../lib/types";

export default function AcceptShare() {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const isPro = useIsPro();
  const [item, setItem] = useState<SharedMealInboxItem | null>(null);
  const [goals, setGoals] = useState<Goals | null>(null);
  const [alreadyEaten, setAlreadyEaten] = useState<MealEstimate["totals"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inbox, g, meals] = await Promise.all([
        loadInbox(),
        loadGoalsRemote(),
        loadMealsRemote()
      ]);
      const found = inbox.find((i) => i.shareId === shareId);
      if (!found) {
        setError("This shared meal is no longer available — it may have been dismissed or already logged.");
        setLoading(false);
        return;
      }
      setItem(found);
      setGoals(g);
      const today = todayISO();
      const todayMeals = meals.filter((m) => localDateOf(m.logged_at) === today);
      setAlreadyEaten(sumTotals(todayMeals));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    load();
  }, [load]);

  async function onConfirm(final: MealEstimate, mealType: MealType) {
    if (!item) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Free users get a stripped version (calories + macros only). Pro
      // users get the full payload including the 25-nutrient panel.
      await acceptShare(item.shareId, final, mealType, { stripNutrients: !isPro });
      navigate("/partners");
    } catch (err) {
      setSaveError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="p-8 text-center text-slate-500 text-sm">Loading shared meal…</div>;
  }
  if (error) {
    return (
      <div className="p-4 space-y-3">
        <BackButton onClick={() => navigate("/partners")} label="Partners" />
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
          {error}
        </div>
      </div>
    );
  }
  if (!item || !goals) return null;

  return (
    <div className="space-y-3">
      <div className="px-4 pt-4 flex items-center justify-between">
        <BackButton onClick={() => navigate("/partners")} label="Cancel" />
        <span className="text-xs text-slate-400">
          From <span className="text-emerald-400">{item.sharerEmail}</span>
        </span>
      </div>
      {item.note && (
        <div className="mx-4 px-3 py-2 rounded-md bg-slate-900 border-l-2 border-violet-400 text-xs text-slate-300 italic">
          "{item.note}"
        </div>
      )}
      <MealReview
        initial={item.meal.estimate}
        initialMealType={item.meal.meal_type}
        disagreementPct={0}
        goals={goals}
        alreadyEatenToday={alreadyEaten ?? undefined}
        saving={saving}
        saveError={saveError}
        onConfirm={onConfirm}
        onCancel={() => navigate("/partners")}
      />
    </div>
  );
}
